import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '@/context/AppContext';
import { useLanguage } from '@/context/LanguageContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CheckCircle2, Home, Clock, Calendar, Receipt, ShieldCheck } from 'lucide-react';

const SuccessPage = () => {
  const navigate = useNavigate();
  const { currentUser, withdrawData, logout } = useApp();
  const { t } = useLanguage();
  const [referenceNumber, setReferenceNumber] = useState('');
  const [submissionDate, setSubmissionDate] = useState('');
  const [showContent, setShowContent] = useState(false);

  useEffect(() => {
    const ref = 'BM' + Date.now().toString().slice(-8) + Math.random().toString(36).substring(2, 6).toUpperCase();
    setReferenceNumber(ref);
    
    const now = new Date();
    setSubmissionDate(now.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }));

    setTimeout(() => setShowContent(true), 300);
  }, []);

  const handleReturnHome = () => {
    logout();
    navigate('/');
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount);
  };

  if (!currentUser || !withdrawData) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center vault-bg p-4">
        <Card className="secure-card rounded-xl p-8 text-center max-w-md">
          <CheckCircle2 className="h-16 w-16 text-emerald-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-[#0A2540] mb-4">{t('sessionExpired')}</h1>
          <Button onClick={() => navigate('/')} className="bg-[#0A2540] hover:bg-[#163454]">
            {t('returnHome')}
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col vault-bg">
      <header className="header-nav px-4 py-3 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto flex items-center justify-center">
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="BMPay" className="h-7 w-7" />
            <span className="font-bold text-[#0A2540] font-['Manrope']">BMPay</span>
          </div>
        </div>
      </header>

      <main className="flex-1 flex flex-col items-center justify-center p-4 sm:p-8">
        <div className={`w-full max-w-md transition-all duration-700 ${showContent ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <Card className="rounded-xl overflow-hidden border-0 shadow-xl">
            <CardContent className="p-0">
              <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 px-6 sm:px-8 py-10 text-center relative overflow-hidden">
                <div className="absolute inset-0 overflow-hidden">
                  <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full animate-pulse"></div>
                  <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-white/10 rounded-full animate-pulse" style={{ animationDelay: '0.5s' }}></div>
                </div>
                
                <div className="relative">
                  <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg animate-bounce" style={{ animationDuration: '2s' }}>
                    <CheckCircle2 className="h-14 w-14 text-emerald-500" />
                  </div>
                  <h1 className="text-2xl sm:text-3xl font-bold text-white font-['Manrope']" data-testid="success-title">
                    {t('transferInProgress')}
                  </h1>
                  <p className="text-emerald-100 mt-2 text-sm">
                    {t('ofProcessing')}
                  </p>
                </div>
              </div>

              <div className="px-6 sm:px-8 py-6 bg-emerald-50 border-b border-emerald-100">
                <p className="text-emerald-800 text-center text-sm leading-relaxed" data-testid="success-message">
                  {t('confirmationByEmail')}
                </p>
              </div>

              <div className="px-6 sm:px-8 py-6 space-y-4">
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                  <div className="flex items-center gap-3 mb-2">
                    <Receipt className="h-5 w-5 text-slate-500" />
                    <span className="text-xs text-slate-500 uppercase tracking-wider font-medium">{t('transferReference')}</span>
                  </div>
                  <p className="font-mono text-lg font-bold text-[#0A2540]" data-testid="reference-number">
                    {referenceNumber}
                  </p>
                </div>

                <div className="bg-[#0A2540] rounded-lg p-4 text-white">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-xs text-white/70 uppercase tracking-wider font-medium">{t('transferAmount')}</span>
                  </div>
                  <p className="font-['Public_Sans'] text-3xl font-bold" data-testid="transfer-amount">
                    {formatAmount(currentUser.amount)} {currentUser.currency}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Clock className="h-4 w-4 text-slate-500" />
                      <span className="text-xs text-slate-500 uppercase tracking-wider">{t('delay')}</span>
                    </div>
                    <p className="font-medium text-[#0A2540]" data-testid="delay-time">{t('delayValue')}</p>
                  </div>
                  
                  <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                    <div className="flex items-center gap-2 mb-2">
                      <Calendar className="h-4 w-4 text-slate-500" />
                      <span className="text-xs text-slate-500 uppercase tracking-wider">{t('date')}</span>
                    </div>
                    <p className="font-medium text-[#0A2540] text-sm" data-testid="submission-date">{submissionDate}</p>
                  </div>
                </div>
              </div>

              <div className="px-6 sm:px-8 pb-6">
                <Button
                  onClick={handleReturnHome}
                  className="w-full h-14 bg-[#0A2540] hover:bg-[#163454] text-white font-semibold text-base rounded-lg transition-all duration-200"
                  data-testid="return-home-button"
                >
                  <Home className="h-5 w-5 mr-2" />
                  {t('returnHome')}
                </Button>
              </div>

              <div className="flex flex-col items-center py-5 bg-slate-50 border-t border-slate-100">
                <div className="security-badge">
                  <ShieldCheck className="h-4 w-4" />
                  <span>{t('secureTransaction')}</span>
                </div>
                <p className="text-xs text-slate-500 mt-2">{t('secureBMPay')}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default SuccessPage;
